export function isBlank(value) {
  return value === null || value === undefined || (typeof value === 'string' && value.trim() === '');
}

export function asArray(value) {
  return Array.isArray(value) ? value : [];
}

export function getDeepValue(obj, path) {
  if (isBlank(path)) return undefined;

  const parts = String(path)
    .split(/[/.]/)
    .map((part) => part.trim())
    .filter(Boolean);

  let current = obj;

  for (const part of parts) {
    if (Array.isArray(current) && /^\d+$/.test(part)) {
      current = current[Number(part)];
    } else if (current !== null && typeof current === 'object' && Object.prototype.hasOwnProperty.call(current, part)) {
      current = current[part];
    } else {
      return undefined;
    }
  }

  return current;
}

export function getDataValue(entry, path) {
  if (!entry || isBlank(path)) return undefined;

  const direct = getDeepValue(entry, path);
  if (!isBlank(direct)) return direct;

  const body = getDeepValue(entry.body, path);
  if (!isBlank(body)) return body;

  const headers = getDeepValue(entry.headers, path);
  if (!isBlank(headers)) return headers;

  return undefined;
}

export function firstAvailable(entry, paths = []) {
  for (const path of paths) {
    const value = getDataValue(entry, path);
    if (!isBlank(value)) return value;
  }
  return undefined;
}

export function toFiniteNumber(value) {
  if (isBlank(value)) return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

export function displayValue(value, fallback = 'Unknown') {
  if (isBlank(value)) return fallback;
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (Array.isArray(value) || (typeof value === 'object' && value !== null)) {
    try {
      return JSON.stringify(value);
    } catch (error) {
      return fallback;
    }
  }
  return String(value);
}

export function getTimestampMillis(value) {
  if (isBlank(value)) return null;

  if (value instanceof Date) {
    const time = value.getTime();
    return Number.isFinite(time) ? time : null;
  }

  if (typeof value === 'object') {
    if (Number.isFinite(value.seconds)) return value.seconds * 1000;
    if (typeof value.toDate === 'function') {
      const date = value.toDate();
      return date instanceof Date ? date.getTime() : null;
    }
  }

  if (typeof value === 'number') {
    return value < 10_000_000_000 ? value * 1000 : value;
  }

  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export function getEntryTimestamp(entry) {
  return getTimestampMillis(entry?.dateCreated ?? entry?.createdAt ?? entry?.timestamp ?? entry?.time);
}

export function formatDateTime(value, fallback = 'Unknown') {
  const millis = getTimestampMillis(value);
  if (!millis) return fallback;
  return new Date(millis).toLocaleString('en-AU');
}

export function latestEntry(DeviceData) {
  return asArray(DeviceData)[0] || null;
}

export function limitRows(DeviceData, limit = 10) {
  const rows = asArray(DeviceData);
  const max = Number(limit);
  const safeLimit = Number.isFinite(max) && max > 0 ? Math.min(max, rows.length) : rows.length;
  return rows.slice(0, safeLimit);
}
